<template>
  <div
    class="checkpoint-edit-form-container edit-checkpoint-form"
    :class="{ 'edit-checkpoint-form--new-inspection': newCheckpointEditAndInspection }"
  >
    <div v-if="newCheckpointEditAndInspection" class="ex-checkpoint-new-edit-chrome">
      <CheckpointEditHeader
        :showPageChrome="!isNewInspectionVideoProcessing"
        :showVideoProcessing="isNewInspectionVideoProcessing"
        :breadcrumbs="breadcrumbs"
        :cardHasChanges="checkpointCardHasChanges"
        :orgBadgeStatus="orgBadgeStatusForCard"
        :statusMenuState="effectiveStatusMenuState"
        :proposedTabIndex="statusMenuProposedTabIndex"
        :statusMenuCheckpointData="checkpoint"
        :level="editHeaderLevel"
        :criteria="criteria"
        :checkpointPrefix="checkpointPrefix"
        :language="editHeaderLanguage"
        :languages="editHeaderLanguages"
        :canTranslate="canTranslateForEditHeader"
        :activate-disabled="activateDisabled"
        @breadcrumb-click="handleBreadcrumbClick"
        @open-chatter="handlePageChromeChatter"
        @card-actions-edit="handlePageChromeCardEdit"
        @update:level="handleEditHeaderLevelUpdate"
        @status-menu-activate="onStatusMenuActivate"
        @status-menu-propose-changes="onStatusMenuProposeChanges"
        @status-menu-publish="onStatusMenuPublish"
        @status-menu-discard="onStatusMenuDiscard"
        @status-menu-tab-change="onStatusMenuTabChange"
        @status-menu-action="onStatusMenuAction"
        @save="handleEditHeaderSave"
      />
    </div>
    <CheckpointHeader
      v-else
      :state="checkpointState"
      :readiness_level="readiness_level"
      :criteria="criteria"
      :checkpointPrefix="checkpointPrefix"
      :instructions="instructions"
      :breadcrumbs="breadcrumbs"
      :checkpointId="checkpointId"
      :responsibilityId="responsibilityId"
      :checkpointLevel="checkpointLevel"
      :checkpointStatus="checkpointStatus"
      :checkpointInstructions="instructionsContentString"
      @breadcrumb-click="handleBreadcrumbClick"
      @checkpoint-updated="handleCheckpointUpdated"
      @open-chatter="openChatter"
    />
    <div
      v-if="showInvalidMicrolearningNotice"
      class="checkpoint-microlearning-warning"
      role="status"
      aria-live="polite"
    >
      <div class="checkpoint-microlearning-warning__icon">
        <v-icon size="20" color="#667085">mdi-information-outline</v-icon>
      </div>
      <div class="checkpoint-microlearning-warning__content">
        <p class="checkpoint-microlearning-warning__title">This Checkpoint has no Microlearning</p>
        <span class="checkpoint-microlearning-warning__cta">
          Propose Changes to add Microlearning
        </span>
      </div>
    </div>
    <div v-if="useCheckpointInspectionInstructions && !showInvalidMicrolearningNotice">
      <PendingTaskToUploadVideoBanner
        v-if="!hideBodyForNewInspectionProcessing && hasPendingVideoUploadTask"
        :assignedUserFirstName="pendingAssignedToFirstName"
        :assignedUserLastName="pendingAssignedToLastName"
        :assignedUserAvatar="pendingAssignedToAvatar"
        :assignedByFirstName="pendingAssignedByFirstName"
        :assignedByLastName="pendingAssignedByLastName"
        :assignedByAvatar="pendingAssignedByAvatar"
        :assignedDate="pendingTaskDeadline"
        @cancel="handlePendingVideoTaskCancel"
        @send-reminder="handlePendingVideoTaskSendReminder"
      />
      <CheckpointInspectionInstructions
        v-show="!hideBodyForNewInspectionProcessing && !hasPendingVideoUploadTask"
        ref="checkpointInspectionInstructionsRef"
        :post-upload-video-reveal-pending="postProposeVideoRevealPending"
        :checkpoint="checkpoint"
        :checkpointInspection="checkpointInspection"
        :checkpointId="checkpointId"
        :responsibilityId="responsibilityId"
        :checkpointPrefix="checkpointPrefix"
        :microlearningVideoId="microlearningVideoId"
        :checkpointCriteria="checkpointCriteria"
        :responsibilityCode="responsibilityCode"
        :responsibilityName="responsibilityName"
        :responsibilityPurpose="responsibilityPurpose"
        :checkpointInstructions="instructionsContentString"
        @video-uploaded="handleVideoUploaded"
        @video-url-ready="handleInspectionVideoUrlReady"
        @refresh-checkpoint="handleRefreshCheckpoint"
        @propose-click="handleProposeKnowledgeArticleClick"
        @proposed-knowledge-article-html-change="handleProposedKnowledgeArticleHtmlChange"
        @proposed-knowledge-article-remove="handleProposedKnowledgeArticleRemove"
        @proposed-knowledge-article-save="handleProposedKnowledgeArticleSave"
        @proposed-knowledge-article-discard="handleProposedKnowledgeArticleDiscard"
      />
    </div>
    <div v-else-if="!hideBodyForNewInspectionProcessing && !showInvalidMicrolearningNotice">
      <CheckpointEditMenu
        :instructions="instructionsState"
        :instructionsContent="instructionsContentData"
        :instructionsIsPublished="instructionsIsPublishedData"
        :instructionsChange="instructionsChangeData"
        :microlearning="microlearningState"
        :microlearningVideoUrl="microlearningVideoUrl"
        :microlearningVideoId="microlearningVideoId"
        :checkpointId="checkpointId"
        :responsibilityId="responsibilityId"
        :checkpointCriteria="checkpointCriteria"
        :checkpointPrefix="checkpointPrefix"
        :checkpointLevel="checkpointLevel"
        :checkpointStatus="checkpointStatus"
        :quizz="quizState"
        :show_quiz="false"
        :responsibilityCode="responsibilityCode"
        :responsibilityName="responsibilityName"
        :responsibilityPurpose="responsibilityPurpose"
        :checkpointInstructions="instructionsContentString"
        :set_to_microlearning_instructions="set_to_microlearning_instructions"
        :microlearningInstructions="microlearningInstructions"
        :instructions_draft="instructions_draft"
        @instructions-publish="handleInstructionsPublish"
        @instructions-discard="handleInstructionsDiscard"
        @instructions-input="handleInstructionsInput"
        @instructions-updated="handleInstructionsUpdated"
        @video-uploaded="handleVideoUploaded"
        @video-deleted="handleVideoDeleted"
        @refresh-checkpoint="handleRefreshCheckpoint"
        :isStorybook="isStorybook"
      />
    </div>
    <ChecklistDrawer
      ref="drawerContainer"
      :drawer="drawer"
      :listLevel="checklistLevel"
      :listCriteria="checklistCriteria"
      :listData="listData"
      :chatterList="chatterList"
      :totalCheckpointChatter="totalCheckpointChatter"
      @closeDrawer="closeDrawer"
      @requestMoreCheckpointsChatter="handleRequestMoreChatter"
    />
    <template v-if="newCheckpointEditAndInspection">
      <component
        :is="dialog.component"
        v-for="dialog in menuDialogConfigs"
        :key="dialog.key"
        :show="dialog.show"
        :name="menuActionCheckpointName"
        v-on="dialog.listeners"
      />
      <ProposeChangesToActiveMicrolearningDialog
        :value="showProposeChangesToActiveMicrolearningDialog"
        :has-active-microlearning="hasActiveMicrolearningForProposeChanges"
        @input="onProposeChangesToActiveMicrolearningDialogInput"
        @start="handleProposeChangesToActiveMicrolearningStart"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, onUnmounted } from "vue";

const checkpointInspectionInstructionsRef = ref(null);
const showProposeChangesToActiveMicrolearningDialog = ref(false);
import CheckpointHeader from "@components/CheckpointHeader/CheckpointHeader.vue";
import CheckpointEditHeader from "@components/CheckpointEditHeader/CheckpointEditHeader.vue";
import CheckpointEditMenu from "@components/CheckpointEditMenu/CheckpointEditMenu.vue";
import { changeCheckpointV2, checkpointStateUpdate, deleteResponsibilityCheckpoint } from "@/api";
import { formatCriteriaPrefix } from "@components/utils/formatCriteriaPrefix";
import ActivateCheckpointDialog from "@components/ActivateCheckpointDialog/ActivateCheckpointDialog.vue";
import DeleteCheckpointDialog from "@components/DeleteCheckpointDialog/DeleteCheckpointDialog.vue";
import RetireCheckpointDialog from "@components/RetireCheckpointDialog/RetireCheckpointDialog.vue";
import ReactivateCheckpointDialog from "@components/ReactivateCheckpointDialog/ReactivateCheckpointDialog.vue";
import ArchiveCheckpointDialog from "@components/ArchiveCheckpointDialog/ArchiveCheckpointDialog.vue";
import UnarchiveCheckpointDialog from "@components/UnarchiveCheckpointDialog/UnarchiveCheckpointDialog.vue";
import ChecklistDrawer from "@components/ChecklistCriteriaTable/ChecklistDrawer.vue";
import CheckpointInspectionInstructions from "@components/CheckpointInspectionInstructions/CheckpointInspectionInstructions.vue";
import PendingTaskToUploadVideoBanner from "@components/PendingTaskToUploadVideoBanner/PendingTaskToUploadVideoBanner.vue";
import ProposeChangesToActiveMicrolearningDialog from "@components/ProposeChangesToActiveMicrolearningDialog/ProposeChangesToActiveMicrolearningDialog.vue";
import store from "../../account/src/store";
import {
  isCheckpointActivateDisabled,
  isProposedVideoKitIncomplete,
} from "@/utils/draftCheckpointValidity";
import {
  hasActiveMicrolearningAsset,
  isProcessingMicrolearningString as isProcessingMicrolearningStringUtil,
  shouldShowInvalidMicrolearningNotice as shouldShowInvalidMicrolearningNoticeUtil,
} from "@/utils/activeCheckpointMicrolearningValidity";
import { getCheckpointVideoSignedUrl } from "@/api/responsibilityAPI";
import { useNotificationStore } from "@/store/pinia/notifications";
import {
  VIDEO_POST_UPLOAD_TOAST_MESSAGE,
  VIDEO_POST_UPLOAD_TOAST_TITLE,
  waitForVideoPostUploadReveal,
} from "@/utils/videoPostUploadReveal";

const notificationStore = useNotificationStore();

const emit = defineEmits([
  "back-to-list",
  "instructions-publish",
  "instructions-discard",
  "instructions-input",
  "instructions-updated",
  "video-uploaded",
  "video-url-ready",
  "video-deleted",
  "refresh-checkpoint",
  "checkpoint-updated",
  "get-chatter-list",
  "requestMoreCheckpointsChatter",
  "pending-video-task-cancel",
  "pending-video-task-send-reminder",
  "status-menu-activate",
  "status-menu-propose-changes",
  "status-menu-publish",
  "status-menu-discard",
  "status-menu-tab-change",
  "status-menu-action",
  "optionClick",
  "card-actions-edit",
  "checkpoint-count-changed",
  "propose-knowledge-article-create",
  "proposed-knowledge-article-html-change",
  "proposed-knowledge-article-remove",
  "proposed-knowledge-article-save",
  "proposed-knowledge-article-discard",
]);

const breadcrumbs = ref([
  { text: "Checklist", href: "" },
  { text: "Checkpoint", href: "", disabled: true },
]);

const handleBreadcrumbClick = (breadcrumb) => {
  if (breadcrumb.text === "Checklist") {
    emit("back-to-list");
  }
};

const props = defineProps({
  checkpoint: {
    type: Object,
    default: null,
  },
  checkpointInspection: {
    type: Object,
    default: null,
  },
  checkpointState: {
    type: String,
    default: "Editable",
  },
  readiness_level: {
    type: Object,
    default: () => ({ id: 1, name: "Apprentice", value: "apprentice" }),
  },
  criteria: {
    type: String,
    default: "",
  },
  instructions: {
    type: String,
    default: "",
  },
  // Video banner props
  videoBannerText: {
    type: String,
    default: "You are a Master if you have at least one current Master badge for a Responsibility",
  },
  videoBannerSubText: {
    type: String,
    default:
      "Learn about what is a master and what is expected of you as a master. We rely on masters to create inspection checklists to be used by coaches to certify readiness of employees as well as train and certify coaches to increase readiness building capacity.",
  },
  videoUrl: {
    type: String,
    default: "https://example.com/video.mp4",
  },
  videoThumbnail: {
    type: String,
    default: "https://placehold.co/160x90",
  },
  instructionsState: {
    type: String,
    default: "add",
  },
  instructionsContent: {
    type: Object,
    default: null,
  },
  instructionsIsPublished: {
    type: Boolean,
    default: false,
  },
  instructionsChange: {
    type: Object,
    default: null,
  },
  microlearningState: {
    type: String,
    default: "add",
  },
  microlearningVideoUrl: {
    type: String,
    default: null,
  },
  microlearningVideoId: {
    type: [String, Number],
    default: null,
  },
  checkpointId: {
    type: [String, Number],
    default: null,
  },
  responsibilityId: {
    type: [String, Number],
    default: null,
  },
  checkpointCriteria: {
    type: String,
    default: "",
  },
  checkpointPrefix: {
    type: String,
    default: "",
  },
  checkpointLevel: {
    type: [Number, String],
    default: 1,
  },
  checkpointStatus: {
    type: String,
    default: "active",
  },
  quizState: {
    type: String,
    default: "add",
  },
  showQuiz: {
    type: Boolean,
    default: true,
  },
  // Chatter props
  chatterList: {
    type: Array,
    default: () => [],
  },
  totalCheckpointChatter: {
    type: Number,
    default: 0,
  },
  responsibilityCode: {
    type: String,
    default: "",
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  responsibilityPurpose: {
    type: String,
    default: "",
  },
  checkpointInstructions: {
    type: String,
    default: "",
  },
  microlearningInstructions: {
    type: [String, Object],
    default: "",
  },
  isStorybook: {
    type: Boolean,
    default: false,
  },
  set_to_microlearning_instructions: {
    type: Boolean,
    default: false,
  },
  instructions_draft: {
    type: String,
    default: "",
  },
  /**
   * When set, forces `CheckpointStatusMenu` `state` (overrides auto-mapping from `checkpointStatus`).
   * Leave unset so draft → draft tab + Activate, active → Active tab + Propose, etc.
   */
  checkpointStatusMenuState: {
    type: String,
    default: null,
    validator: (v) =>
      v == null ||
      [
        "draft_activate_enabled",
        "draft_activate_disabled",
        "active_propose",
        "proposed_viewing_active",
        "proposed_drafting",
        "proposed_publish_disabled",
      ].includes(v),
  },
  /** When dual-tab proposed UI is used; `null` uses the menu default. */
  statusMenuProposedTabIndex: {
    type: Number,
    default: null,
  },
});

const hasActiveMicrolearningForProposeChanges = computed(() =>
  hasActiveMicrolearningAsset(props.checkpoint)
);

const drawer = ref(false);
const checklistLevel = ref("");
const checklistCriteria = ref("");
const listData = ref({});

const instructionsContentData = computed(() => {
  if (props.instructionsContent) {
    return props.instructionsContent;
  }
  if (props.instructions) {
    return { content: props.instructions };
  }
  return null;
});

const checkpointInspectionKit = computed(() => {
  return store.getters["featureFlags/ideaMDP427"];
});
const newCheckpointEditAndInspection = computed(() => {
  return store.getters["featureFlags/ideaMDP452"];
});
const useCheckpointInspectionInstructions = computed(
  () => checkpointInspectionKit.value || newCheckpointEditAndInspection.value
);

/** Microlearning string is in an encoding / pending state (aligns with CheckpointEditMenu / inspection flow). */
function isProcessingMicrolearningString(value) {
  return isProcessingMicrolearningStringUtil(value);
}

const postProposeVideoRevealPending = ref(false);

/** When the new edit header is on, show the compact loader only during the post-upload reveal window. */
const isNewInspectionVideoProcessing = computed(() => {
  if (!newCheckpointEditAndInspection.value) {
    return false;
  }
  return postProposeVideoRevealPending.value;
});

onUnmounted(() => {
  postProposeVideoRevealPending.value = false;
});

/** Hides instructions / edit menu so the processing layout is not duplicated. */
const hideBodyForNewInspectionProcessing = computed(
  () => newCheckpointEditAndInspection.value && isNewInspectionVideoProcessing.value
);

const LEVEL_NAMES = new Set(["Apprentice", "Professional", "Coach", "Master"]);

const editHeaderLevel = computed(() => {
  const n = props.readiness_level?.name;
  if (n && LEVEL_NAMES.has(n)) {
    return n;
  }
  return "Apprentice";
});

const ENGLISH = Object.freeze({ id: 1, name: "English", code: "en" });

const editHeaderLanguage = computed(() => ENGLISH);
const editHeaderLanguages = computed(() => []);
const canTranslateForEditHeader = computed(() => false);

const currentCheckpointStatus = computed(() =>
  String(props.checkpoint?.status || props.checkpointStatus || "").toLowerCase()
);

const isCheckpointActive = computed(() => currentCheckpointStatus.value === "active");

const showInvalidMicrolearningNotice = computed(() =>
  shouldShowInvalidMicrolearningNoticeUtil(props.checkpoint, {
    newInspectionUi: newCheckpointEditAndInspection.value,
    status: currentCheckpointStatus.value,
  })
);

const activateDisabled = computed(() => {
  const checkpoint = props.checkpoint || {};

  if (newCheckpointEditAndInspection.value) {
    return isCheckpointActivateDisabled(checkpoint);
  }

  return isProposedVideoKitIncomplete(checkpoint);
});

/**
 * Syncs `CheckpointStatusMenu` with `checkpointStatus` (and later optional checkpoint fields).
 * Default was always `active_propose`, which showed "Active Checkpoint" even when the badge was Draft.
 */
const effectiveStatusMenuState = computed(() => {
  if (props.checkpointStatusMenuState) {
    return props.checkpointStatusMenuState;
  }
  const s = (props.checkpointStatus || "active").toString().toLowerCase();
  if (s === "draft") {
    if (newCheckpointEditAndInspection.value && activateDisabled.value) {
      return "draft_activate_disabled";
    }
    return "draft_activate_enabled";
  }
  if (s === "active") {
    return "active_propose";
  }
  return "active_propose";
});

const checkpointCardHasChanges = computed(() => instructionsChangeData.value != null);

const orgBadgeStatusForCard = computed(() => {
  const s = (props.checkpointStatus || "active").toString().toLowerCase();
  if (["draft", "active", "retired", "inactive", "archived", "pending"].includes(s)) {
    return s;
  }
  return "active";
});

const isEditHeaderSaving = ref(false);
const showActivateCheckpointDialog = ref(false);
const showDeleteCheckpointDialog = ref(false);
const showRetireCheckpointDialog = ref(false);
const showReActivateCheckpointDialog = ref(false);
const showArchiveCheckpointDialog = ref(false);
const showUnarchiveCheckpointDialog = ref(false);
const currentCheckPoint = ref(null);
const activateFromMenuAction = ref(false);

const activateDialogCheckpointName = computed(
  () => (props.criteria && String(props.criteria)) || props.checkpoint?.criteria || ""
);
const menuActionCheckpointName = computed(
  () => currentCheckPoint.value?.criteria || activateDialogCheckpointName.value || ""
);

const menuDialogConfigs = computed(() => [
  {
    key: "activate",
    component: ActivateCheckpointDialog,
    show: showActivateCheckpointDialog.value,
    listeners: {
      onActivate: handleActivateDialogConfirm,
      onClose: closeActivateCheckpointDialog,
    },
  },
  {
    key: "delete",
    component: DeleteCheckpointDialog,
    show: showDeleteCheckpointDialog.value,
    listeners: {
      onDelete: handleConfirmDeleteFromMenu,
      onClose: () => (showDeleteCheckpointDialog.value = false),
    },
  },
  {
    key: "retire",
    component: RetireCheckpointDialog,
    show: showRetireCheckpointDialog.value,
    listeners: {
      onRetire: handleConfirmRetireFromMenu,
      onClose: () => (showRetireCheckpointDialog.value = false),
    },
  },
  {
    key: "reactivate",
    component: ReactivateCheckpointDialog,
    show: showReActivateCheckpointDialog.value,
    listeners: {
      onReactivate: handleConfirmReactivateFromMenu,
      onClose: () => (showReActivateCheckpointDialog.value = false),
    },
  },
  {
    key: "archive",
    component: ArchiveCheckpointDialog,
    show: showArchiveCheckpointDialog.value,
    listeners: {
      onArchive: handleConfirmArchiveFromMenu,
      onClose: () => (showArchiveCheckpointDialog.value = false),
    },
  },
  {
    key: "unarchive",
    component: UnarchiveCheckpointDialog,
    show: showUnarchiveCheckpointDialog.value,
    listeners: {
      onUnarchive: handleConfirmUnarchiveFromMenu,
      onClose: () => (showUnarchiveCheckpointDialog.value = false),
    },
  },
]);

function getAccountIdFromStore() {
  try {
    const stored = localStorage.getItem("currentAccountID");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Error parsing currentAccountID from localStorage:", e);
  }
  return null;
}

const handlePageChromeChatter = () => {
  openChatter({
    id: props.checkpointId,
    level: props.readiness_level?.name || "Apprentice",
    criteria: props.criteria,
    prefix: props.checkpointPrefix,
    instructions: instructionsContentString.value,
  });
};

const handlePageChromeCardEdit = () => {
  emit("card-actions-edit");
};

function closeActivateCheckpointDialog() {
  showActivateCheckpointDialog.value = false;
  activateFromMenuAction.value = false;
}

function onStatusMenuActivate() {
  if (activateDisabled.value) {
    return;
  }
  activateFromMenuAction.value = false;
  currentCheckPoint.value = JSON.parse(JSON.stringify(props.checkpoint || {}));
  showActivateCheckpointDialog.value = true;
}
function openProposeChangesToActiveMicrolearningDialog() {
  showProposeChangesToActiveMicrolearningDialog.value = true;
}

function onProposeChangesToActiveMicrolearningDialogInput(open) {
  showProposeChangesToActiveMicrolearningDialog.value = open;
}

function handleProposeChangesToActiveMicrolearningStart(payload) {
  showProposeChangesToActiveMicrolearningDialog.value = false;
  const instructions = checkpointInspectionInstructionsRef.value;
  if (instructions?.applyProposeChangesOption) {
    instructions.applyProposeChangesOption(payload);
    return;
  }
  const option = payload?.option;
  if (option === "new_article") {
    handleProposeKnowledgeArticleCreate();
  }
}

function onStatusMenuProposeChanges() {
  openProposeChangesToActiveMicrolearningDialog();
}
function onStatusMenuPublish() {
  emit("status-menu-publish");
}
function onStatusMenuDiscard() {
  emit("status-menu-discard");
}
function onStatusMenuTabChange(index) {
  emit("status-menu-tab-change", index);
}
function setCurrentCheckpointFromPayload(payload) {
  const rawData = payload?.data || props.checkpoint || null;
  currentCheckPoint.value = rawData ? JSON.parse(JSON.stringify(rawData)) : null;
}
function onStatusMenuAction(payload) {
  const action = payload?.item?.value;
  setCurrentCheckpointFromPayload(payload);

  switch (action) {
    case "activate":
      if (activateDisabled.value) {
        return;
      }
      activateFromMenuAction.value = true;
      showActivateCheckpointDialog.value = true;
      return;
    case "delete":
      showDeleteCheckpointDialog.value = true;
      return;
    case "retire":
      showRetireCheckpointDialog.value = true;
      return;
    case "reactivate":
      showReActivateCheckpointDialog.value = true;
      return;
    case "archive":
      showArchiveCheckpointDialog.value = true;
      return;
    case "unarchive":
      showUnarchiveCheckpointDialog.value = true;
      return;
    case "move":
      emit("optionClick", {
        item: { value: "move_checkpoint", name: "Move" },
        data: currentCheckPoint.value,
        showDialog: true,
      });
      return;
  }
  emit("status-menu-action", payload);
}

const levelNameToProficiency = {
  Apprentice: 1,
  Professional: 2,
  Coach: 3,
  Master: 4,
};

async function handleEditHeaderSave(payload) {
  if (!props.checkpointId || !props.responsibilityId) {
    return;
  }
  const levelName = payload?.level;
  const proficiencyFromLevel =
    levelName && levelNameToProficiency[levelName] != null
      ? levelNameToProficiency[levelName]
      : Number(props.checkpointLevel) || 1;

  isEditHeaderSaving.value = true;
  try {
    const accountId = getAccountIdFromStore();

    if (accountId) {
      const response = await changeCheckpointV2(
        accountId,
        props.responsibilityId,
        props.checkpointId,
        {
          criteria: payload.criteria,
          prefix: formatCriteriaPrefix(payload.prefix),
          instructions: instructionsContentString.value || "<p></p>",
          proficiency_level: proficiencyFromLevel,
          status: props.checkpointStatus,
          account: accountId,
          responsibility: props.responsibilityId,
        }
      );
      emit("checkpoint-updated", response?.data || null);
    }
  } catch (error) {
    console.error("Error updating checkpoint:", error);
  } finally {
    isEditHeaderSaving.value = false;
  }
}

function splitDisplayName(name) {
  if (!name || typeof name !== "string") {
    return { first: "", last: "" };
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return { first: parts[0], last: "" };
  }
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

const hasPendingVideoUploadTask = computed(() => {
  const c = props.checkpoint;
  if (!c) return false;
  return Boolean(c.assigned_by && c.assigned_to && c.deadline);
});

const pendingTaskDeadline = computed(() => props.checkpoint?.deadline ?? null);

const pendingAssignedToFirstName = computed(
  () => splitDisplayName(props.checkpoint?.assigned_to?.name).first
);
const pendingAssignedToLastName = computed(
  () => splitDisplayName(props.checkpoint?.assigned_to?.name).last
);
const pendingAssignedToAvatar = computed(() => props.checkpoint?.assigned_to?.avatar ?? null);

const pendingAssignedByFirstName = computed(
  () => splitDisplayName(props.checkpoint?.assigned_by?.name).first
);
const pendingAssignedByLastName = computed(
  () => splitDisplayName(props.checkpoint?.assigned_by?.name).last
);
const pendingAssignedByAvatar = computed(() => props.checkpoint?.assigned_by?.avatar ?? null);

const handlePendingVideoTaskCancel = () => {
  emit("pending-video-task-cancel", { checkpoint: props.checkpoint });
};

const handlePendingVideoTaskSendReminder = () => {
  emit("pending-video-task-send-reminder", { checkpoint: props.checkpoint });
};

const instructionsIsPublishedData = computed(() => {
  if (props.instructionsIsPublished) {
    return props.instructionsIsPublished;
  }
  return props.instructionsState === "selected";
});

const instructionsContentString = computed(() => {
  if (props.instructionsContent && props.instructionsContent.content) {
    return props.instructionsContent.content;
  }
  if (props.instructions) {
    return props.instructions;
  }
  return "";
});

const instructionsChangeData = computed(() => {
  if (props.instructionsChange) {
    return props.instructionsChange;
  }
  if (props.instructions_draft) {
    const strippedContent = props.instructions_draft
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .replace(/\s+/g, "")
      .trim();
    if (strippedContent.length > 0) {
      return { content: props.instructions_draft };
    }
  }
  return null;
});

async function handleConfirmActivateCheckpoint() {
  showActivateCheckpointDialog.value = false;
  activateFromMenuAction.value = false;
  if (!props.checkpointId || !props.responsibilityId) {
    return;
  }
  const accountId = getAccountIdFromStore();
  if (!accountId) {
    return;
  }
  try {
    const response = await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      props.checkpointId,
      { status: "active" }
    );
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("Error activating checkpoint:", error);
    emit("refresh-checkpoint");
  }
}

function handleActivateDialogConfirm() {
  if (activateFromMenuAction.value) {
    handleConfirmActivateFromMenu();
    return;
  }
  void handleConfirmActivateCheckpoint();
}

async function handleConfirmDeleteFromMenu() {
  showDeleteCheckpointDialog.value = false;
  const accountId = getAccountIdFromStore();
  const checkpointId = currentCheckPoint.value?.id ?? props.checkpointId;
  if (!accountId || !props.responsibilityId || !checkpointId) {
    return;
  }
  try {
    await deleteResponsibilityCheckpoint(accountId, props.responsibilityId, checkpointId);
    emit("checkpoint-count-changed");
    emit("back-to-list");
    emit("refresh-checkpoint");
  } catch (error) {
    console.error("Error deleting checkpoint:", error);
  }
}

async function handleConfirmActivateFromMenu() {
  showActivateCheckpointDialog.value = false;
  activateFromMenuAction.value = false;
  const accountId = getAccountIdFromStore();
  const checkpointId = currentCheckPoint.value?.id ?? props.checkpointId;
  if (!accountId || !props.responsibilityId || !checkpointId) {
    return;
  }
  try {
    const response = await changeCheckpointV2(accountId, props.responsibilityId, checkpointId, {
      status: "active",
    });
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("Error activating checkpoint:", error);
    emit("refresh-checkpoint");
  }
}

async function handleConfirmRetireFromMenu() {
  showRetireCheckpointDialog.value = false;
  const accountId = getAccountIdFromStore();
  const checkpointId = currentCheckPoint.value?.id ?? props.checkpointId;
  if (!accountId || !props.responsibilityId || !checkpointId) {
    return;
  }
  try {
    const response = await checkpointStateUpdate(
      accountId,
      props.responsibilityId,
      checkpointId,
      "retire"
    );
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("Error retiring checkpoint:", error);
    emit("refresh-checkpoint");
  }
}

async function handleConfirmReactivateFromMenu() {
  showReActivateCheckpointDialog.value = false;
  const accountId = getAccountIdFromStore();
  const checkpointId = currentCheckPoint.value?.id ?? props.checkpointId;
  if (!accountId || !props.responsibilityId || !checkpointId) {
    return;
  }
  try {
    const response = await checkpointStateUpdate(
      accountId,
      props.responsibilityId,
      checkpointId,
      "reactivate"
    );
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("Error reactivating checkpoint:", error);
    emit("refresh-checkpoint");
  }
}

async function handleConfirmArchiveFromMenu() {
  showArchiveCheckpointDialog.value = false;
  const accountId = getAccountIdFromStore();
  const checkpointId = currentCheckPoint.value?.id ?? props.checkpointId;
  if (!accountId || !props.responsibilityId || !checkpointId) {
    return;
  }
  try {
    const response = await changeCheckpointV2(accountId, props.responsibilityId, checkpointId, {
      status: "archived",
    });
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("Error archiving checkpoint:", error);
    emit("refresh-checkpoint");
  }
}

async function handleConfirmUnarchiveFromMenu(updatedName) {
  showUnarchiveCheckpointDialog.value = false;

  const checkpointData = { ...(currentCheckPoint.value || {}) };
  const trimmedName = updatedName ? updatedName.trim() : "";
  const originalCriteria = checkpointData?.criteria ? checkpointData.criteria.trim() : "";
  if (trimmedName && trimmedName !== originalCriteria) {
    checkpointData.criteria = trimmedName;
    checkpointData._nameUpdated = true;
  }

  const accountId = getAccountIdFromStore();
  const checkpointId = checkpointData?.id ?? props.checkpointId;
  if (!accountId || !props.responsibilityId || !checkpointId) {
    return;
  }

  const payload = {
    status: "retired",
  };
  if (checkpointData._nameUpdated && checkpointData.criteria && checkpointData.criteria.trim()) {
    payload.criteria = checkpointData.criteria.trim();
  }
  try {
    const response = await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      checkpointId,
      payload
    );
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
    emit("checkpoint-count-changed");
  } catch (error) {
    console.error("Error unarchiving checkpoint:", error);
    emit("refresh-checkpoint");
  }
}

async function handleEditHeaderLevelUpdate(nextLevel) {
  if (!newCheckpointEditAndInspection.value) {
    return;
  }
  if (!props.checkpointId || !props.responsibilityId) {
    return;
  }
  if (nextLevel == null || !levelNameToProficiency[nextLevel]) {
    return;
  }
  if (editHeaderLevel.value === nextLevel) {
    return;
  }
  try {
    const accountId = getAccountIdFromStore();
    if (!accountId) {
      return;
    }
    const response = await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      props.checkpointId,
      {
        criteria: props.criteria,
        prefix: formatCriteriaPrefix(props.checkpointPrefix || "Can"),
        instructions: instructionsContentString.value || "<p></p>",
        proficiency_level: levelNameToProficiency[nextLevel],
        status: props.checkpointStatus,
        account: accountId,
        responsibility: props.responsibilityId,
      }
    );
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
  } catch (error) {
    console.error("Error updating checkpoint level:", error);
    emit("refresh-checkpoint");
  }
}

const handleInstructionsPublish = (content) => {
  emit("instructions-publish", content);
};

const handleInstructionsDiscard = () => {
  emit("instructions-discard");
};

const handleInstructionsInput = (content) => {
  emit("instructions-input", content);
};

const handleInstructionsUpdated = (data) => {
  emit("instructions-updated", data);
};

const handleVideoUploaded = (payload) => {
  emit("video-uploaded", payload);
};

const handleVideoDeleted = () => {
  emit("video-deleted");
};

const handleRefreshCheckpoint = () => {
  emit("refresh-checkpoint");
};

async function handleInspectionVideoUrlReady(payload) {
  if (!newCheckpointEditAndInspection.value) {
    emit("video-url-ready", payload);
    emit("refresh-checkpoint");
    return;
  }

  postProposeVideoRevealPending.value = true;
  notificationStore.info(VIDEO_POST_UPLOAD_TOAST_TITLE, VIDEO_POST_UPLOAD_TOAST_MESSAGE, 8000);

  const accountId = getAccountIdFromStore();
  const revealResult = await waitForVideoPostUploadReveal({
    getSignedUrl: async () => {
      if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
        return payload?.signedUrl ?? null;
      }
      const response = await getCheckpointVideoSignedUrl(
        accountId,
        props.responsibilityId,
        props.checkpointId
      );
      return response?.data?.url ?? payload?.signedUrl ?? null;
    },
  });

  const readyPayload = {
    ...payload,
    signedUrl: revealResult.signedUrl ?? payload?.signedUrl ?? null,
  };

  emit("refresh-checkpoint");
  emit("video-url-ready", readyPayload);
  postProposeVideoRevealPending.value = false;
}

async function handleProposeKnowledgeArticleClick() {
  const status = (props.checkpointStatus || "").toString().toLowerCase();
  if (status !== "draft") {
    return;
  }
  if (!props.checkpointId || !props.responsibilityId) {
    emit("propose-knowledge-article-create");
    return;
  }
  const accountId = getAccountIdFromStore();
  if (!accountId) {
    emit("propose-knowledge-article-create");
    return;
  }
  try {
    const response = await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      props.checkpointId,
      {
        proposed_microlearning_type: "article",
      }
    );
    if (response?.data) {
      emit("checkpoint-updated", response.data);
    } else {
      emit("refresh-checkpoint");
    }
  } catch (error) {
    console.error("Error setting proposed_microlearning_type to article:", error);
    return;
  }
  emit("propose-knowledge-article-create");
}

function handleProposedKnowledgeArticleHtmlChange(payload) {
  emit("proposed-knowledge-article-html-change", payload);
}

function handleProposedKnowledgeArticleRemove() {
  emit("proposed-knowledge-article-remove");
}

function handleProposedKnowledgeArticleSave(payload) {
  emit("proposed-knowledge-article-save", payload);
}

function handleProposedKnowledgeArticleDiscard() {
  emit("proposed-knowledge-article-discard");
}

const handleCheckpointUpdated = (data) => {
  emit("checkpoint-updated", data);
};

const openChatter = (data) => {
  checklistLevel.value = data.level;
  checklistCriteria.value = data.criteria;
  listData.value = data;
  drawer.value = true;
  emit("get-chatter-list", data);
};

const closeDrawer = (data) => {
  drawer.value = false;
};

const handleRequestMoreChatter = (event) => {
  emit("requestMoreCheckpointsChatter", event);
};
</script>

<style scoped>
.checkpoint-edit-form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.checkpoint-edit-form-container .guide-video-banner {
  margin-top: 0px;
}
.edit-checkpoint-form {
  padding: 24px 12px 12px 12px;
}

.edit-checkpoint-form--new-inspection {
  gap: 0;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #fff;
  box-shadow:
    0 1px 3px 0 rgba(16, 24, 40, 0.1),
    0 1px 2px 0 rgba(16, 24, 40, 0.06);
  margin: 12px;
  padding-top: 0;
}

.checkpoint-microlearning-warning {
  margin: 4px 0px 0px 0px;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.checkpoint-microlearning-warning__icon {
  display: flex;
  width: 40px;
  height: 40px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.checkpoint-microlearning-warning__content {
  min-width: 0;
}

.checkpoint-microlearning-warning__title {
  margin: 0 0 6px;
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.checkpoint-microlearning-warning__cta {
  padding: 0;
  border: 0;
  background: transparent;
  color: #6941c6;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}
</style>
